// src/lib/filters/rpc.ts
//
// Boundary normalizer for the `filter_contacts` Supabase RPC.
//
// CLIENT SHAPE (saved views, in-memory state):     SERVER SHAPE (RPC payload):
//   { valueEnd, matchYear, ... }                ⇒   { end_value, match_year, ... }
//
// Pure functions. No side effects. The wire ≠ client invariant is intentional —
// `toRpcCondition` is the single bridge. Saved views keep storing `valueEnd`;
// translation happens at RPC dispatch (see `src/hooks/useContacts.ts`).
//
// Background: prior to this module, `valueEnd` (camelCase) was sent straight to
// the RPC, which reads `end_value` (snake_case). End-of-range was always NULL
// on the server, silently breaking every `between` date filter while the UI
// still reported "Filters applied successfully". Regression dates to 2026-02-06.

import type { ContactFilters } from '@/types/contact';
import { computeAbsoluteDateFromRelative } from './relative-dates';

/**
 * Client-side FilterCondition. The `ContactFilters` type in `@/types/contact`
 * intentionally omits `matchYear` (added later in the UI module), so we widen
 * here to include it. All other fields mirror the in-memory / saved-view shape.
 */
export interface FilterCondition {
  id: string;
  table?: string;
  field: string;
  operator: string;
  value: string;
  /** Client-side end-of-range. Renamed to `end_value` on the wire. */
  valueEnd?: string;
  /** Only meaningful for `important_dates.date`. Renamed to `match_year` on the wire. */
  matchYear?: boolean;
  /** Only meaningful for `important_dates.date`: also require this label on the same row.
   *  Passes through to the wire unchanged (not in CLIENT_ONLY_DROPPED_KEYS). */
  label?: string;
  // Client-only relative-date storage (resolved to absolute `value`/`valueEnd` before dispatch).
  dateMode?: 'absolute' | 'relative';
  relativeValue?: number;
  relativeUnit?: 'days';
  relativeDirection?: 'ago' | 'from_now';
  relativeValueEnd?: number;
  relativeDirectionEnd?: 'ago' | 'from_now';
}

/**
 * RPC-shaped filter condition. Snake-case wire fields; client-only fields stripped.
 */
export interface RpcFilterCondition {
  id: string;
  table?: string;
  field: string;
  operator: string;
  value: string;
  /** Snake-case wire name. The SQL function reads `end_value`. */
  end_value?: string;
  /** Snake-case wire name. SQL function reads `match_year`. */
  match_year?: boolean;
  /** Correlated label for `important_dates.date` (same wire name; SQL reads `label`). */
  label?: string;
}

export interface RpcFilterGroup {
  id: string;
  /** Snake-case wire name. SQL reads `group_type`. */
  group_type: 'AND' | 'OR';
  conditions: RpcFilterCondition[];
}

export interface RpcContactFilters {
  search: string;
  group_type: 'AND' | 'OR';
  groups: RpcFilterGroup[];
}

/**
 * Client-only fields that exist for UI state and saved-view persistence but
 * have no meaning on the wire. Stripped from the RPC payload.
 */
const CLIENT_ONLY_DROPPED_KEYS: ReadonlySet<string> = new Set([
  'valueEnd',             // renamed → end_value
  'matchYear',            // renamed → match_year
  'dateMode',             // client-only: UI tab state for relative-vs-absolute
  'relativeValue',        // client-only: relative-date input (resolved to absolute `value` in toRpcCondition)
  'relativeUnit',         // client-only
  'relativeDirection',    // client-only
  'relativeValueEnd',     // client-only: between-end relative input (resolved to absolute `valueEnd` in toRpcCondition)
  'relativeDirectionEnd', // client-only
]);

/**
 * Normalize a client-side FilterCondition to the RPC wire shape.
 *
 * - Renames `valueEnd` → `end_value`
 * - Renames `matchYear` → `match_year` (only when explicitly true; SQL coalesces absence)
 * - Drops camelCase originals and other client-only storage fields
 */
export function toRpcCondition(c: FilterCondition): RpcFilterCondition {
  const out: Record<string, unknown> = {};

  // Resolve relative-date conditions to an absolute `value`/`valueEnd` HERE, at
  // the wire boundary, because the relative metadata (dateMode/relativeValue/…)
  // is stripped below. Callers that dispatch a filter without first resolving
  // relative dates (the quick-filter presets in FilterNavMenu, saved views)
  // would otherwise send `value:''`, and the `filter_contacts` RPC builds
  // `<col> < ''::timestamptz` → Postgres 22007 "invalid input syntax" → the
  // whole contacts load errors. This is the single choke point every RPC
  // dispatch routes through, so resolving here covers them all. Coalescing
  // mirrors applyFilters() in ContactFilters.tsx.
  let value = c.value;
  let valueEnd = c.valueEnd;
  if (c.dateMode === 'relative') {
    const unit = c.relativeUnit || 'days';
    const relDir = c.relativeDirection ?? 'ago';
    value = computeAbsoluteDateFromRelative(c.relativeValue ?? 0, unit, relDir);
    if (c.operator === 'between') {
      const dirEnd = c.relativeDirectionEnd ?? relDir;
      valueEnd = computeAbsoluteDateFromRelative(c.relativeValueEnd ?? 0, unit, dirEnd);
    }
  }

  // Pass-through everything that isn't on the dropped list.
  for (const [key, val] of Object.entries(c)) {
    if (CLIENT_ONLY_DROPPED_KEYS.has(key)) continue;
    out[key] = val;
  }
  out.value = value;

  if (valueEnd !== undefined) {
    out.end_value = valueEnd;
  }

  if (c.matchYear === true) {
    out.match_year = true;
  }

  return out as unknown as RpcFilterCondition;
}

/**
 * Normalize a top-level ContactFilters object to the RPC payload shape.
 *
 * - Trims `search`
 * - Defaults top-level `group_type` to 'AND'
 * - For each group: maps the legacy `type` field → `group_type` and normalizes
 *   every condition via `toRpcCondition`.
 */
export function toRpcFilters(filters: ContactFilters): RpcContactFilters {
  return {
    search: filters.search?.trim() || '',
    group_type: filters.group_type || 'AND',
    groups: (filters.groups || []).map((group) => {
      // `group.type` is the canonical client field; some legacy callers may
      // already pass `group_type` directly. Accept both.
      const groupAny = group as { type?: 'AND' | 'OR'; group_type?: 'AND' | 'OR' };
      return {
        id: group.id,
        group_type: groupAny.group_type || groupAny.type || 'AND',
        conditions: group.conditions.map((c) => toRpcCondition(c as FilterCondition)),
      };
    }),
  };
}
